import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { Business as BusinessIcon } from '@mui/icons-material'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import React, { useEffect, useMemo, useState } from 'react'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { resolvePickupLocationById } from '../../util/pickupLocationDisplay'

interface SelectCompanyForLeadDialogProps {
  open: boolean
  /** All companies from the `contact_companies` collection. The dialog filters to memberships internally. */
  companies: ContactCompany[]
  /** Id of the contact we're creating the lead for; only companies that include them are listed. */
  contactId: string
  /** Display name of the contact for context in the title. */
  contactName: string
  onClose: () => void
  /** Receives the chosen company record. */
  onConfirm: (company: ContactCompany) => void
  /** Dialog title. Defaults to lead copy. */
  title?: string
}

/**
 * Forces the user to pick a {@link ContactCompany} the contact is already
 * associated with before creating a lead. Rendered as a dropdown rather than
 * a search box, so the option set is obviously bounded by the contact's
 * existing memberships.
 */
const SelectCompanyForLeadDialog: React.FC<SelectCompanyForLeadDialogProps> = ({
  open,
  companies,
  contactId,
  contactName,
  onClose,
  onConfirm,
  title = 'Pick a company for this lead',
}) => {
  const theme = useTheme()
  const accent = theme.palette.primary.main
  const { pickupLocations } = usePickupLocations()

  /** Companies where this contact is a member, alphabetized for predictable order. */
  const associatedCompanies = useMemo(
    () =>
      companies
        .filter((c) => c.members.some((m) => m.contactId === contactId))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [companies, contactId],
  )

  const [selectedId, setSelectedId] = useState<string>('')

  useEffect(() => {
    if (!open) return
    setSelectedId(associatedCompanies.length === 1 ? associatedCompanies[0].id : '')
  }, [open, associatedCompanies])

  const canSubmit = Boolean(selectedId)

  const handleConfirm = () => {
    const picked = associatedCompanies.find((c) => c.id === selectedId)
    if (!picked) return
    onConfirm(picked)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              bgcolor: alpha(accent, 0.15),
              color: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BusinessIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              For {contactName || 'this contact'}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {associatedCompanies.length === 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This contact isn't associated with any companies yet. Add them to a
              company first from the Companies tab.
            </Alert>
          ) : null}
          <FormControl fullWidth disabled={associatedCompanies.length === 0}>
            <InputLabel id="lead-company-select-label">Company</InputLabel>
            <Select
              labelId="lead-company-select-label"
              label="Company"
              value={selectedId}
              onChange={(e) => setSelectedId(String(e.target.value))}
              autoFocus
            >
              {associatedCompanies.map((company) => {
                const businessLocation = resolvePickupLocationById(
                  pickupLocations,
                  company.businessAddressId,
                )
                const city = businessLocation?.city?.trim()
                const stateAbbr = businessLocation?.state?.trim()
                const locText = [city, stateAbbr].filter(Boolean).join(', ')
                return (
                  <MenuItem key={company.id} value={company.id}>
                    <Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {company.name || 'Unnamed company'}
                      </Typography>
                      {locText || company.website ? (
                        <Typography variant="caption" color="text.secondary">
                          {[locText, company.website].filter(Boolean).join(' • ')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </MenuItem>
                )
              })}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!canSubmit}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SelectCompanyForLeadDialog
